/**
 * S4 · GATES — the one write.
 *
 * The copy states SCORE-ALWAYS semantics first, because "I turned it off and
 * it's still showing posts" is a real trap in this system. Each toggle's
 * description states its COUNTER-INTUITIVE half — fails-closed /
 * mesh-off-means-deaf / score-always — never a restatement of its name.
 *
 * Apply confirmation is EVIDENCE-BASED. Nothing observable changes when the
 * bot re-reads `runtime_toggles`, so the panel does not pretend: it says
 * "written, apply unobservable" and upgrades to CONFIRMED the moment a case
 * appears whose decision reflects the new gate. Honest by construction, zero
 * source changes (P12 would turn it into a real readback).
 *
 * TENANCY DEPARTURE (ARCHITECTURE §4 · D-T2): the READ is a customer
 * surface — it explains `no_trade_reason: source_disabled` on their own case
 * — and the WRITE is admin-only, because `runtime_toggles` is NODE-GLOBAL:
 * one bot, one set of kill switches. A customer flipping `truth_social`
 * would stop the machine for every tenant.
 *
 * The read-only explanation sits DIRECTLY under the lede, not at the foot of
 * the list: six faded controls with their reason below the fold reads as a
 * failed load, not as a deliberate refusal.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiError, type GateRow } from "../lib/api";
import { stamp } from "../lib/time";

/** Canonical order. The set is small, complete and unpaginated, so ordering
 *  it here is presentation — not a number computed from a partial page. */
const ORDER = ["truth_social", "telegram_farsna", "axios", "mesh", "cerebras_live", "notifications"];

export function Gates({ admin }: { admin: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["gates"], queryFn: api.gates });
  const [err, setErr] = useState<string | null>(null);

  const flip = useMutation({
    mutationFn: ({ name, enabled }: { name: string; enabled: boolean }) => api.setGate(name, enabled),
    onSuccess: () => {
      setErr(null);
      void qc.invalidateQueries({ queryKey: ["gates"] });
    },
    onError: (e: unknown) => {
      const a = e as ApiError;
      setErr(
        a?.status === 403
          ? "read-only: the kill switches are node-global and admin-owned"
          : a?.status === 503
            ? "runtime_toggles is unavailable — during a parallel run the bot's database is mounted read-only"
            : `write failed: ${a?.message ?? "unknown"}`,
      );
    },
  });

  // An unexpected toggle is surfaced at the end, never dropped: a new
  // migration must become visible here without a code change.
  const rank = (n: string) => (ORDER.indexOf(n) < 0 ? ORDER.length : ORDER.indexOf(n));
  const gates = [...(q.data?.gates ?? [])].sort((a, b) => rank(a.name) - rank(b.name) || a.name.localeCompare(b.name));
  const writable = !!q.data?.writable && admin;
  const off = gates.filter((g) => !g.enabled).length;

  return (
    <section className="surface gates">
      <header className="surface__head">
        <h1 className="surface__title">Gates</h1>
        <div className="surface__meta mono">
          {gates.length} switches{off > 0 ? ` · ${off} off` : ""}
        </div>
      </header>

      <p className="surface__lede">
        {q.data?.headline ?? "These stop TRADING, not scoring."} Posts still arrive, score and show. Only the TRADE is
        withheld.
      </p>

      <div className="surface__body">
        {!writable && (
          <div
            className="callout"
            title="the state is shown because it explains `no_trade_reason: source_disabled` on your own cases"
          >
            read-only: the kill switches are node-global — one bot, one set — so the write is the operator&apos;s.
          </div>
        )}

        {q.isError && (
          <div
            className="callout callout--alarm"
            title="the console never creates or migrates that table — a missing table is a 503 that says exactly that"
          >
            runtime_toggles unavailable
          </div>
        )}

        {q.data?.degraded && (
          <div className="callout callout--stale" data-degraded="audit">
            {q.data.degraded_note}
          </div>
        )}

        {gates.map((g) => (
          <Gate
            key={g.name}
            gate={g}
            writable={writable}
            auditable={!q.data?.degraded}
            onFlip={(v) => flip.mutate({ name: g.name, enabled: v })}
          />
        ))}

        {err && <div className="callout callout--alarm">{err}</div>}
      </div>
    </section>
  );
}

function Gate({
  gate,
  writable,
  auditable,
  onFlip,
}: {
  gate: GateRow;
  writable: boolean;
  auditable: boolean;
  onFlip: (v: boolean) => void;
}) {
  const apply = gate.apply;
  return (
    <div className="gate" data-gate={gate.name} data-enabled={gate.enabled}>
      <button
        className={`switch${gate.enabled ? " switch--on" : ""}`}
        disabled={!writable}
        aria-pressed={gate.enabled}
        aria-label={`${gate.name} ${gate.enabled ? "on" : "off"}`}
        onClick={() => onFlip(!gate.enabled)}
      >
        <span className="switch__track" aria-hidden="true">
          <span className="switch__knob" />
        </span>
        <span className="switch__state">{gate.enabled ? "ON" : "OFF"}</span>
      </button>
      <div className="gate__body">
        <div className="gate__name mono" title={gate.note ?? undefined}>
          {gate.name}
          {gate.fails_closed && (
            <span className="chip" title="a node that has not applied the migration cannot fire">
              fails CLOSED
            </span>
          )}
        </div>
        {/* The three source gates all carry the same score-always sentence the
            LEDE just said — printed per-row it was the page's residual copy
            offender (audit, 2026-07-30). Boilerplate moves to the hover;
            notes that say something the lede does not (mesh-off-means-deaf,
            fails-closed, incidents-stay-silent) stay visible. */}
        {gate.note && !gate.note.startsWith("score-always") ? (
          <p className="note">{gate.note}</p>
        ) : null}
        {gate.updated_at ? (
          <div className="note">
            written <span className="mono">{stamp(gate.updated_at)}</span> by{" "}
            <span className="mono">{gate.updated_by ?? "—"}</span>
          </div>
        ) : (
          !auditable && (
            // NOT "—": an absent audit column and an untouched switch are
            // different facts, and only one of them is about this gate.
            <p className="note" title="this node's runtime_toggles has no audit columns">
              no write history
            </p>
          )
        )}
        {apply && (
          <div className="note">
            {apply.state === "CONFIRMED" ? (
              <>
                ✓ APPLY CONFIRMED — case <span className="mono">{apply.evidence_case?.post_id}</span> scored with{" "}
                <code className="mono">no_trade_reason: source_disabled</code>
              </>
            ) : (
              // NOT prefixed "written ·": the audit line directly above already
              // opens with "written <timestamp> by <who>", and the same word
              // labelling two different things read as one fact printed twice.
              // The note states the pending-apply fact on its own.
              <>{apply.note}</>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
